import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Home as HomeIcon, Image, Users, ShoppingCart,
  Ticket, Settings, Scissors, LogOut, Menu, X, ExternalLink, Mail, Layout, RotateCcw,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/website", label: "Website Editor", icon: Layout },
  { to: "/admin/courses", label: "Courses", icon: BookOpen },
  { to: "/admin/homepage", label: "Homepage", icon: HomeIcon },
  { to: "/admin/media", label: "Media & Reviews", icon: Image },
  { to: "/admin/students", label: "Students", icon: Users },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/refunds", label: "Refunds", icon: RotateCcw, badgeKey: "refunds" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/community", label: "Community", icon: Users },
  { to: "/admin/messages", label: "Messages", icon: Mail, badgeKey: "messages" },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pendingRefunds, setPendingRefunds] = useState(0);

  useEffect(() => {
    const load = () => api.get("/admin/messages/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    const loadRefunds = () => api.get("/admin/refunds").then(({ data }) => setPendingRefunds(data.pending || 0)).catch(() => {});
    load(); loadRefunds();
    const t = setInterval(() => { load(); loadRefunds(); }, 30000);
    const onFocus = () => { load(); loadRefunds(); };
    const onChange = (e) => { if (e.detail && typeof e.detail.unread === "number") setUnread(e.detail.unread); else load(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("efa:messages-unread-changed", onChange);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); window.removeEventListener("efa:messages-unread-changed", onChange); };
  }, []);

  const Nav = () => (
    <nav className="flex flex-col gap-1 p-3">
      {LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${l.label.toLowerCase().split(" ")[0]}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
              isActive ? "bg-[#DCA846] text-black font-semibold" : "text-zinc-300 hover:bg-white/5"
            }`}>
          <l.icon className="h-4 w-4" /> <span className="flex-1">{l.label}</span>
          {l.badgeKey === "messages" && unread > 0 && (
            <span data-testid="admin-messages-badge"
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DCA846] px-1.5 text-[10px] font-bold text-black">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
          {l.badgeKey === "refunds" && pendingRefunds > 0 && (
            <span data-testid="admin-refunds-badge"
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DCA846] px-1.5 text-[10px] font-bold text-black">
              {pendingRefunds > 99 ? "99+" : pendingRefunds}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Topbar */}
      <div className="flex h-16 items-center justify-between border-b border-white/10 px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setOpen(true)} className="lg:hidden"><Menu className="h-6 w-6" /></button>
          <Link to="/admin" className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-[#DCA846]" />
            <span className="font-display font-bold uppercase tracking-wider">Admin<span className="text-[#DCA846]">Panel</span></span>
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" target="_blank" className="hidden sm:flex items-center gap-1 text-sm text-zinc-400 hover:text-[#DCA846]"><ExternalLink className="h-4 w-4" /> View Site</Link>
          <span className="hidden sm:block text-sm text-zinc-400">{user?.name}</span>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-zinc-400 hover:text-red-400"><LogOut className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="flex">
        <aside className="hidden w-64 shrink-0 border-r border-white/10 lg:block min-h-[calc(100vh-4rem)]"><Nav /></aside>
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64 bg-zinc-950 border-r border-white/10">
              <button onClick={() => setOpen(false)} className="absolute right-3 top-3"><X className="h-5 w-5" /></button>
              <div className="pt-12"><Nav /></div>
            </div>
          </div>
        )}
        <main className="flex-1 min-w-0 p-4 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
